import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useInventory } from '../context/InventoryContext';
import { FaFileExcel, FaFilePdf, FaFilter, FaEye, FaChevronDown, FaChevronUp, FaSearch, FaChevronLeft, FaChevronRight, FaGasPump, FaSave, FaTrophy } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import api from "../services/api";

const InstallerReports = () => {
  const { user, currentUser, users = [] } = useAuth() || {};
  const { vehicles = [] } = useInventory() || {};
  const userRole = currentUser?.role || user?.role || "Super Admin";
  const userId = currentUser?.id || currentUser?.user_id || user?.id || user?.user_id;

  const isSuperAdmin = userRole === 'Super Admin' || userRole === 'super_admin';
  const isTeamLead = userRole === 'teamlead';
  const isAdmin = userRole === 'admin';

  const [installers, setInstallers] = useState([]);
  const [filteredInstallers, setFilteredInstallers] = useState([]);
  const [dateRange, setDateRange] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [installerFilter, setInstallerFilter] = useState('');
  const [installerSearch, setInstallerSearch] = useState('');
  const [installerNames, setInstallerNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageType, setImageType] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState('');
  const dropdownRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [editingPrices, setEditingPrices] = useState({});
  const [savingPrice, setSavingPrice] = useState({});
  const [globalFuelPrice, setGlobalFuelPrice] = useState('');
  const [isGlobalUpdate, setIsGlobalUpdate] = useState(false);

  // Leaderboard state
  const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);
  const [allQueriesData, setAllQueriesData] = useState([]);

  // ✅ Get ALL installers from users
  const allInstallers = useMemo(() => {
    return users.filter(u => u.role === 'installer');
  }, [users]);

  // ✅ Create a map of installer ID to mileage for quick lookup
  const installerMileageMap = useMemo(() => {
    const map = {};
    allInstallers.forEach(installer => {
      map[installer.id] = installer.mileage || null;
      map[installer.username?.toLowerCase()] = installer.mileage || null;
    });
    return map;
  }, [allInstallers]);

  // ✅ Get installer profile mileage by installer ID or username
  const getInstallerMileage = (installerId, installerName) => {
    if (installerId && installerMileageMap[installerId]) {
      return installerMileageMap[installerId];
    }
    if (installerName) {
      const normalizedName = installerName.toLowerCase().trim();
      const installer = allInstallers.find(
        u => u.username?.toLowerCase() === normalizedName
      );
      if (installer) {
        return installer.mileage || null;
      }
      if (installerMileageMap[normalizedName]) {
        return installerMileageMap[normalizedName];
      }
    }
    return null;
  };

  // ✅ NEW: Helper function to calculate fuel consumption
  const calculateFuelConsumption = (totalKm, mileage) => {
    if (!totalKm || totalKm <= 0 || !mileage || mileage <= 0) {
      return null;
    }
    return totalKm / mileage;
  };

  // ✅ UPDATED: Get effective fuel consumption (from session or calculated)
  const getEffectiveFuelConsumption = (item) => {
    if (item.fuelConsumption && item.fuelConsumption > 0) {
      return item.fuelConsumption;
    }
    if (item.totalKmTravelled && item.totalKmTravelled > 0 && item.mileage && item.mileage > 0) {
      const calculated = item.totalKmTravelled / item.mileage;
    
      return calculated;
    }
    return null;
  };

  // ✅ Get assigned installer IDs
  const assignedInstallerIds = useMemo(() => {
    if (!isTeamLead || !users) return [];
    
    const teamLeadId = currentUser?.id || currentUser?.user_id || user?.id || user?.user_id;
    
    if (!teamLeadId) {
     
      return [];
    }
    
    const ids = users
      .filter(u => {
        if (u.role !== 'installer') return false;
        const tlId = u.teamLeadId || u.teamlead_id;
        return String(tlId) === String(teamLeadId);
      })
      .map(u => String(u.id));
    
    return ids;
  }, [users, isTeamLead, currentUser, user]);

  const assignedInstallerUsernames = useMemo(() => {
    if (!isTeamLead || !users) return [];
    
    const teamLeadId = currentUser?.id || currentUser?.user_id || user?.id || user?.user_id;
    
    if (!teamLeadId) return [];
    
    const usernames = users
      .filter(u => {
        if (u.role !== 'installer') return false;
        const tlId = u.teamLeadId || u.teamlead_id;
        return String(tlId) === String(teamLeadId);
      })
      .map(u => String(u.username || '').toLowerCase().trim());
    
    return usernames;
  }, [users, isTeamLead, currentUser, user]);

 // ✅ Fetch all queries for leaderboard using the dedicated endpoint
const fetchAllQueries = async () => {
  if (!currentUser) return [];
  
  
  // Method 1: Try the dedicated leaderboard endpoint
  try {
    const response = await api.get("/queries/all-for-leaderboard", {
      params: {
        companyId: currentUser?.companyId
      }
    });
    if (Array.isArray(response.data) && response.data.length > 0) {
      return response.data;
    }
  } catch (error) {
    console.log('Method 1 failed:', error.message);
  }

  // Method 2: Try to get all queries without username filter
  try {
    const response = await api.get("/queries", {
      params: {
        companyId: currentUser?.companyId,
      }
    });
    if (Array.isArray(response.data) && response.data.length > 0) {

      return response.data;
    }
  } catch (error) {
    console.log('Method 2 failed:', error.message);
  }

  // Method 3: Try with role as 'installer' and no username
  try {
    const response2 = await api.get("/queries", {
      params: {
        role: 'installer',
        companyId: currentUser?.companyId,
      }
    });
    if (Array.isArray(response2.data) && response2.data.length > 0) {

      return response2.data;
    }
  } catch (error) {
    console.log('Method 3 failed:', error.message);
  }

  // Method 4: Try without any params
  try {
    const response4 = await api.get("/queries");
    if (Array.isArray(response4.data) && response4.data.length > 0) {

      return response4.data;
    }
  } catch (error) {
    console.log('Method 4 failed:', error.message);
  }

  return [];
};
  // ✅ Load all queries for leaderboard
  useEffect(() => {
    const loadQueries = async () => {
      const queries = await fetchAllQueries();
      if (queries.length > 0) {
        setAllQueriesData(queries);
     
      }
    };
    loadQueries();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}-${month}-${year} ${hours}:${minutes}`;
    } catch (e) {
      return dateString;
    }
  };

  const calculateDistance = (loginReading, logoutReading) => {
    if (
      loginReading === null ||
      loginReading === undefined ||
      logoutReading === null ||
      logoutReading === undefined
    ) {
      return "N/A";
    }

    const login = Number(loginReading);
    const logout = Number(logoutReading);

    if (isNaN(login) || isNaN(logout)) {
      return "N/A";
    }

    const distance = (logout - login).toFixed(2);
    
    if (parseFloat(distance) < 0) {
      return "N/A";
    }

    return distance;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

 const loadInstallerData = async () => {
  setLoading(true);
  try {
    const userId = currentUser?.id || currentUser?.user_id || user?.id || user?.user_id;
    const userRole = currentUser?.role || user?.role || "Super Admin";
    const isTeamLead = userRole === 'teamlead';
    
    let url = "/installer-session";
    if (isTeamLead && userId) {
      url += `?teamLeadId=${userId}`;
    }
    
    const sessionRes = await api.get(url);
    const BASE_URL = "https://cccs.cordonnx.com";

    // ✅ Helper function to construct image URLs properly
    const constructImageUrl = (imagePath) => {
      if (!imagePath) return null;
      
      // If it's already a full URL
      if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        // Fix the domain if it's wrong
        if (imagePath.includes('cordonnxt.com')) {
          return imagePath.replace('cordonnxt.com', 'cordonnx.com');
        }
        return imagePath;
      }
      
      // If it's just a path, prepend base URL
      const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
      return `${BASE_URL}/${cleanPath}`;
    };

    let formatted = sessionRes.data.map(item => {
      const installerName = item.installer_name || item.name || "N/A";
      const installerId = item.installer_id || item.id || null;

      const installerProfileMileage = getInstallerMileage(installerId, installerName);
      const effectiveMileage = installerProfileMileage || item.vehicle_mileage || null;

      return {
        id: item.id,
        installer_id: installerId,
        installerName: installerName,
        loginDateTime: item.login_time || null,
        loginOdometerImage: constructImageUrl(item.login_odometer_image),
        loginOdometerReading: item.login_odometer_reading || null,
        logoutDateTime: item.logout_time || null,
        logoutOdometerImage: constructImageUrl(item.logout_odometer_image),
        logoutOdometerReading: item.logout_odometer_reading || null,
        totalKmTravelled: Number(item.total_km_travelled) || 0,
        mileage: effectiveMileage,
        sessionMileage: item.vehicle_mileage || null,
        fuelConsumption: item.fuel_consumption || null,
        fuelPrice: item.fuel_price || null,
        totalCost: item.total_cost || null
      };
    });

    // Rest of the function remains the same...
    if (isTeamLead) {
      const filtered = formatted.filter(item => {
        const idMatch = item.installer_id && assignedInstallerIds.includes(String(item.installer_id));
        const nameMatch = item.installerName && assignedInstallerUsernames.includes(String(item.installerName).toLowerCase().trim());
        return idMatch || nameMatch;
      });
      formatted = filtered;
    }

    setInstallers(formatted);
    
    const uniqueInstallers = [...new Set(formatted.map(d => d.installerName).filter(Boolean))].sort();
    setInstallerNames(uniqueInstallers);
    
  } catch (error) {
    console.error("Error loading installer data:", error);
    setInstallers([]);
    setInstallerNames([]);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    loadInstallerData();
  }, [isTeamLead, assignedInstallerIds, assignedInstallerUsernames]);

  // Filter data
  useEffect(() => {
    let filtered = [...installers];
    
    if (installerFilter) {
      filtered = filtered.filter(d => 
        String(d.installerName).toLowerCase().trim() === String(installerFilter).toLowerCase().trim()
      );
    }

    if (installerSearch.trim()) {
      filtered = filtered.filter(d =>
        String(d.installerName || '')
          .toLowerCase()
          .includes(installerSearch.toLowerCase())
      );
    }
    
    const now = new Date();
    let startDateObj = null;
    let endDateObj = new Date();
    
    if (dateRange === '7days') {
      startDateObj = new Date();
      startDateObj.setDate(now.getDate() - 7);
    } else if (dateRange === '15days') {
      startDateObj = new Date();
      startDateObj.setDate(now.getDate() - 15);
    } else if (dateRange === '1month') {
      startDateObj = new Date();
      startDateObj.setMonth(now.getMonth() - 1);
    } else if (dateRange === '3months') {
      startDateObj = new Date();
      startDateObj.setMonth(now.getMonth() - 3);
    } else if (dateRange === 'calendar' && startDate && endDate) {
      startDateObj = new Date(startDate);
      startDateObj.setHours(0, 0, 0, 0);
      endDateObj = new Date(endDate);
      endDateObj.setHours(23, 59, 59, 999);
    }
    
    if (startDateObj) {
      filtered = filtered.filter(d => {
        const deviceDate = d.loginDateTime ? new Date(d.loginDateTime) : new Date(0);
        if (dateRange === 'calendar') {
          return deviceDate >= startDateObj && deviceDate <= endDateObj;
        }
        return deviceDate >= startDateObj;
      });
    }
    
    setFilteredInstallers(filtered);
    setCurrentPage(1);
  }, [installers, dateRange, startDate, endDate, installerFilter, installerSearch]);

  const filteredInstallerNames = installerNames.filter(name =>
    name.toLowerCase().includes(dropdownSearch.toLowerCase())
  );

  const totalPages = Math.ceil(filteredInstallers.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredInstallers.slice(indexOfFirstRow, indexOfLastRow);

  const handleFuelPriceChange = (sessionId, value) => {
    setEditingPrices(prev => ({
      ...prev,
      [sessionId]: value
    }));
  };

  const saveFuelPrice = async (sessionId) => {
    const price = editingPrices[sessionId];
    
    if (price === undefined || price === null || price === '') {
      alert('Please enter a fuel price');
      return;
    }

    const numericPrice = Number(price);
    if (isNaN(numericPrice) || numericPrice < 0) {
      alert('Please enter a valid positive number');
      return;
    }

    setSavingPrice(prev => ({ ...prev, [sessionId]: true }));

    try {
      const response = await api.put("/installer-session/fuel-price", {
        sessionId: sessionId,
        fuelPrice: numericPrice
      });

      if (response.data) {
        const updatedData = response.data;
        
        setInstallers(prev => 
          prev.map(item => {
            if (item.id === sessionId) {
              let fuelConsumption = updatedData.fuel_consumption;
              if ((!fuelConsumption || fuelConsumption === 0) && item.totalKmTravelled && item.mileage && item.mileage > 0) {
                fuelConsumption = item.totalKmTravelled / item.mileage;
               
              }
              
              let totalCost = updatedData.total_cost;
              if ((!totalCost || totalCost === 0) && fuelConsumption && fuelConsumption > 0 && numericPrice > 0) {
                totalCost = fuelConsumption * numericPrice;
               
              }
              
              if (!totalCost || totalCost === 0) {
                const fc = item.fuelConsumption || fuelConsumption;
                if (fc && fc > 0 && numericPrice > 0) {
                  totalCost = fc * numericPrice;
                 
                }
              }
              
            
              
              return {
                ...item,
                fuelPrice: numericPrice,
                fuelConsumption: fuelConsumption || item.fuelConsumption,
                totalCost: totalCost
              };
            }
            return item;
          })
        );
        
        setEditingPrices(prev => {
          const newState = { ...prev };
          delete newState[sessionId];
          return newState;
        });

      }
    } catch (error) {
      console.error('❌ Error updating fuel price:', error);
      alert(error.response?.data?.error || 'Failed to update fuel price');
    } finally {
      setSavingPrice(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  const applyGlobalFuelPrice = async () => {
    if (!globalFuelPrice || globalFuelPrice === '') {
      alert('Please enter a fuel price');
      return;
    }

    const numericPrice = Number(globalFuelPrice);
    if (isNaN(numericPrice) || numericPrice < 0) {
      alert('Please enter a valid positive number');
      return;
    }

    const rowsToUpdate = filteredInstallers.filter(item => 
      !item.fuelPrice || item.fuelPrice === null || item.fuelPrice === ''
    );

    if (rowsToUpdate.length === 0) {
      alert('✅ All rows already have fuel prices set! No updates needed.');
      return;
    }

    if (!window.confirm(`Apply fuel price ₹${numericPrice}/L to ${rowsToUpdate.length} sessions that don't have fuel price set?`)) {
      return;
    }

    setIsGlobalUpdate(true);

    try {
      const updatePromises = rowsToUpdate.map(item => 
        api.put("/installer-session/fuel-price", {
          sessionId: item.id,
          fuelPrice: numericPrice
        })
      );

      const results = await Promise.all(updatePromises);
      
      setInstallers(prev => 
        prev.map(item => {
          const updated = results.find(r => r.data && r.data.id === item.id);
          if (updated) {
            const updatedData = updated.data;
            
            let fuelConsumption = updatedData.fuel_consumption;
            if ((!fuelConsumption || fuelConsumption === 0) && item.totalKmTravelled && item.mileage && item.mileage > 0) {
              fuelConsumption = item.totalKmTravelled / item.mileage;
            }
            
            let totalCost = updatedData.total_cost;
            if ((!totalCost || totalCost === 0) && fuelConsumption && fuelConsumption > 0 && numericPrice > 0) {
              totalCost = fuelConsumption * numericPrice;
            }
            
            if (!totalCost || totalCost === 0) {
              const fc = item.fuelConsumption || fuelConsumption;
              if (fc && fc > 0 && numericPrice > 0) {
                totalCost = fc * numericPrice;
              }
            }
            
            return {
              ...item,
              fuelPrice: numericPrice,
              fuelConsumption: fuelConsumption || item.fuelConsumption,
              totalCost: totalCost
            };
          }
          return item;
        })
      );

      alert(`✅ Fuel price applied to ${results.length} sessions successfully!`);
      setGlobalFuelPrice('');
    } catch (error) {
      console.error('❌ Error applying global fuel price:', error);
      alert('Failed to apply global fuel price to sessions');
    } finally {
      setIsGlobalUpdate(false);
    }
  };

  // ✅ UPDATED: Export to Excel with correct fuel consumption calculation
  const exportToExcel = () => {
    if (filteredInstallers.length === 0) {
      alert("No data available to export!");
      return;
    }

    const exportData = filteredInstallers.map(d => {
      const distance = calculateDistance(d.loginOdometerReading, d.logoutOdometerReading);
      let fuelConsumption = d.fuelConsumption;
      if (!fuelConsumption && d.totalKmTravelled && d.mileage && d.mileage > 0) {
        fuelConsumption = d.totalKmTravelled / d.mileage;
      }
      return {
        'Installer Name': d.installerName,
        'Login Date/Time': formatDate(d.loginDateTime),
        'Login Odometer Reading': d.loginOdometerReading || 'N/A',
        'Logout Date/Time': formatDate(d.logoutDateTime),
        'Logout Odometer Reading': d.logoutOdometerReading || 'N/A',
        'Total Distance Travelled': distance,
        'Mileage (km/L)': d.mileage || '-',
        'Fuel Consumption (L)': fuelConsumption ? Number(fuelConsumption).toFixed(2) : '-',
        'Fuel Price (₹)': d.fuelPrice || '-',
        'Cost (₹)': d.totalCost ? Number(d.totalCost).toFixed(2) : '-',
      };
    });
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    ws['!cols'] = [
      { wch: 25 }, { wch: 22 }, { wch: 22 }, { wch: 22 }, 
      { wch: 22 }, { wch: 20 }, { wch: 20 }, { wch: 20 },
      { wch: 20 }, { wch: 20 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Installer Reports');
    XLSX.writeFile(wb, `installer_report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ✅ UPDATED: Export to PDF with correct fuel consumption calculation
  const exportToPDF = () => {
    if (filteredInstallers.length === 0) {
      alert("No data available to export!");
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4');
    doc.setFontSize(18);
    doc.text('Installer Reports', 14, 18);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 26);
    doc.text(`Date Range Filter: ${getDateRangeText()}`, 14, 32);
    if (installerFilter) doc.text(`Installer Filter: ${installerFilter}`, 14, 38);
    const startY = installerFilter ? 44 : 38;
    doc.text(`Total Records: ${filteredInstallers.length}`, 14, startY);
    
    const tableData = filteredInstallers.map(d => {
      const distance = calculateDistance(d.loginOdometerReading, d.logoutOdometerReading);
      let fuelConsumption = d.fuelConsumption;
      if (!fuelConsumption && d.totalKmTravelled && d.mileage && d.mileage > 0) {
        fuelConsumption = d.totalKmTravelled / d.mileage;
      }
      return [
        d.installerName,
        formatDate(d.loginDateTime),
        d.loginOdometerReading || 'N/A',
        formatDate(d.logoutDateTime),
        d.logoutOdometerReading || 'N/A',
        distance,
        d.mileage || '-',
        fuelConsumption ? Number(fuelConsumption).toFixed(2) : '-',
        d.fuelPrice || '-',
        d.totalCost ? Number(d.totalCost).toFixed(2) : '-',
      ];
    });
    
    doc.autoTable({
      startY: startY + 6,
      head: [['Installer Name', 'Login Date/Time', 'Login Reading', 'Logout Date/Time', 'Logout Reading', 'Total Distance', 'Mileage', 'Fuel Consumption', 'Fuel Price', 'Cost']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229], textColor: 255, fontSize: 7, fontStyle: 'bold' },
      styles: { fontSize: 6.5, cellPadding: 2, overflow: 'linebreak' },
      columnStyles: { 
        0: { cellWidth: 22 }, 1: { cellWidth: 18 }, 2: { cellWidth: 16 }, 
        3: { cellWidth: 18 }, 4: { cellWidth: 16 }, 5: { cellWidth: 16 },
        6: { cellWidth: 16 }, 7: { cellWidth: 16 }, 8: { cellWidth: 14 }, 
        9: { cellWidth: 14 }
      }
    });
    
    doc.save(`installer_report_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const getDateRangeText = () => {
    if (dateRange === '7days') return 'Last 7 days';
    if (dateRange === '15days') return 'Last 15 days';
    if (dateRange === '1month') return 'Last 1 month';
    if (dateRange === '3months') return 'Last 3 months';
    if (dateRange === 'calendar') return `${startDate} to ${endDate}`;
    return 'All time';
  };

// ✅ Leaderboard data calculation with panic buttons - ONLY COMPLETED QUERIES
const leaderboardData = useMemo(() => {
  if (!Array.isArray(users)) return [];

  let allInstallers = users.filter(u => String(u.role).trim().toLowerCase() === "installer");

  const queriesForLeaderboard = allQueriesData.length > 0 ? allQueriesData : [];

  return allInstallers.map(installer => {
    // Total installed vehicles
    const installedCount = Array.isArray(vehicles) ? vehicles.filter(v =>
      String(v.installer_id) === String(installer.id) ||
      String(v.installed_by || '').toLowerCase() === String(installer.username).toLowerCase()
    ).length : 0;

    // ✅ ONLY COMPLETED queries attended by this installer
    const queriesAttended = queriesForLeaderboard.filter(q => {
      const qInstaller = String(q.installerName || q.installer_name || '').toLowerCase().trim();
      const installerName = String(installer.username).toLowerCase().trim();
      const status = String(q.status || '').toLowerCase();
      
      // ✅ ONLY count queries with status 'completed', 'installed', or 'in use'
      const isCompleted = status === 'completed' || status === 'installed' || status === 'in use';
      
      return qInstaller === installerName && isCompleted;
    }).length;

    // Panic buttons installed
    let totalPanicButtons = 0;
    if (Array.isArray(vehicles)) {
      vehicles.forEach(v => {
        const isInstalledByThisInstaller = 
          String(v.installer_id) === String(installer.id) ||
          String(v.installed_by || '').toLowerCase() === String(installer.username).toLowerCase();
        if (isInstalledByThisInstaller) {
          const panicCount = parseInt(v.panic_buttons) || 0;
          totalPanicButtons += panicCount;
        }
      });
    }

    return {
      id: installer.id,
      username: installer.username,
      installedCount: installedCount,
      queriesAttended: queriesAttended,
      totalPanicButtons: totalPanicButtons
    };
  }).sort((a, b) => b.installedCount - a.installedCount);
}, [users, vehicles, allQueriesData]);

  const ImageViewerModal = ({ imageUrl, imageType, onClose }) => {
  if (!imageUrl) return null;
  
  // ✅ Inline SVG fallback - no external dependencies
  const fallbackImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%23EF4444'/%3E%3Ctext x='50%25' y='50%25' font-size='24' fill='%23FFFFFF' text-anchor='middle' dy='.3em'%3EImage%20Not%20Available%3C/text%3E%3C/svg%3E";
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">{imageType} Odometer Image</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition text-2xl leading-none">✕</button>
        </div>
        <div className="p-6 flex items-center justify-center bg-gray-50">
          <img 
            src={imageUrl} 
            alt={`${imageType} Odometer`}
            className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
            onError={(e) => {
              // ✅ Use inline fallback instead of external service
              e.target.src = fallbackImage;
            }}
          />
        </div>
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 text-center">
          Click outside the image or press the ✕ button to close
        </div>
      </div>
    </div>
  );
};

  const handleImageClick = (imageUrl, type) => {
    if (imageUrl) {
      setSelectedImage(imageUrl);
      setImageType(type);
    }
  };

  if (!isSuperAdmin && !isTeamLead && !isAdmin) {
    return (
      <div className="flex items-center justify-center h-[60vh] bg-gray-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg max-w-md">
          <div className="text-red-500 text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h1>
          <p className="text-gray-600">This page is only accessible to Super Admin, Admin, and Team Lead users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto bg-gray-50 min-h-screen font-sans">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Installer Reports Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isTeamLead 
              ? `Viewing reports for your assigned installers (${assignedInstallerIds.length} installers assigned)` 
              : 'View and export installer performance reports with filtering options'}
          </p>
        </div>
        {/* Leaderboard button - Only visible to Super Admin */}
        {isSuperAdmin && (
          <button
            onClick={() => setShowLeaderboardModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold transition shadow-sm"
          >
            <FaTrophy className="text-base" />
            Leaderboard
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <FaFilter className="text-indigo-500" /> Filter Installer Records
        </h2>
        
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Quick Select Range</label>
            <select
              value={dateRange}
              onChange={(e) => {
                setDateRange(e.target.value);
                if (e.target.value !== 'calendar') {
                  setStartDate('');
                  setEndDate('');
                }
              }}
              className="border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 min-w-[160px]"
            >
              <option value="all">All Time</option>
              <option value="7days">Last 7 Days</option>
              <option value="15days">Last 15 Days</option>
              <option value="1month">Last 1 Month</option>
              <option value="3months">Last 3 Months</option>
              <option value="calendar">Custom Date Range</option>
            </select>
          </div>

          {dateRange === 'calendar' && (
            <>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </>
          )}

          {(isSuperAdmin || isAdmin) && (
            <div className="relative" ref={dropdownRef}>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Filter by Installer</label>
              <div 
                className="relative cursor-pointer"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <div className="flex items-center justify-between border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm min-w-[200px] hover:border-indigo-400 transition">
                  <span className={`${installerFilter ? 'text-gray-700' : 'text-gray-400'}`}>
                    {installerFilter || 'Select Installer'}
                  </span>
                  {isDropdownOpen ? (
                    <FaChevronUp className="text-gray-400 ml-2" />
                  ) : (
                    <FaChevronDown className="text-gray-400 ml-2" />
                  )}
                </div>
              </div>
              
              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                      <input
                        type="text"
                        value={dropdownSearch}
                        onChange={(e) => setDropdownSearch(e.target.value)}
                        placeholder="Search installers..."
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-48">
                    {filteredInstallerNames.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-400 text-center">
                        No installers found
                      </div>
                    ) : (
                      <>
                        <div 
                          className={`px-4 py-2 text-sm hover:bg-indigo-50 cursor-pointer transition ${
                            !installerFilter ? 'bg-indigo-50 text-indigo-600 font-medium' : 'text-gray-700'
                          }`}
                          onClick={() => {
                            setInstallerFilter('');
                            setDropdownSearch('');
                            setIsDropdownOpen(false);
                          }}
                        >
                          All Installers
                        </div>
                        {filteredInstallerNames.map((name) => (
                          <div 
                            key={name}
                            className={`px-4 py-2 text-sm hover:bg-indigo-50 cursor-pointer transition ${
                              installerFilter === name ? 'bg-indigo-50 text-indigo-600 font-medium' : 'text-gray-700'
                            }`}
                            onClick={() => {
                              setInstallerFilter(name);
                              setDropdownSearch('');
                              setIsDropdownOpen(false);
                            }}
                          >
                            {name}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {(isSuperAdmin || isAdmin) && (
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1 flex items-center gap-1">
                <FaGasPump className="text-indigo-500" /> Global Fuel Price (₹/L)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={globalFuelPrice}
                  onChange={(e) => setGlobalFuelPrice(e.target.value)}
                  placeholder="Enter fuel price..."
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 w-[160px]"
                  disabled={isGlobalUpdate}
                />
                <button
                  onClick={applyGlobalFuelPrice}
                  disabled={isGlobalUpdate || !globalFuelPrice}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold transition disabled:opacity-50 flex items-center gap-1"
                >
                  {isGlobalUpdate ? (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <FaSave />
                  )}
                  Apply to Blank Rows
                </button>
              </div>
            </div>
          )}

          {(installerFilter || installerSearch) && (
            <div>
              <button
                onClick={() => {
                  setInstallerFilter('');
                  setInstallerSearch('');
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition"
              >
                Clear Filters
              </button>
            </div>
          )}

          <div className="flex gap-2 ml-auto">
            <button
              onClick={exportToExcel}
              disabled={filteredInstallers.length === 0}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold shadow-sm transition ${
                filteredInstallers.length === 0 
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
            >
              <FaFileExcel /> Export Excel
            </button>
            <button
              onClick={exportToPDF}
              disabled={filteredInstallers.length === 0}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold shadow-sm transition ${
                filteredInstallers.length === 0 
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              <FaFilePdf /> Export PDF
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50/50">
          <h2 className="text-base font-semibold text-gray-800">Installer Details</h2>
          <span className="text-xs font-semibold bg-gray-200 text-gray-700 px-2.5 py-1 rounded-full">
            Records Found: {filteredInstallers.length}
          </span>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-gray-100 text-xs font-bold text-gray-500 uppercase border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3">Installer Name</th>
                  <th className="px-4 py-3">Login Date/Time</th>
                  <th className="px-4 py-3 text-center">Login Odometer Image</th>
                  <th className="px-4 py-3 text-center">Login Odometer Reading</th>
                  <th className="px-4 py-3">Logout Date/Time</th>
                  <th className="px-4 py-3 text-center">Logout Odometer Image</th>
                  <th className="px-4 py-3 text-center">Logout Odometer Reading</th>
                  <th className="px-4 py-3 text-center">Total Distance Travelled</th>
                  <th className="px-4 py-3 text-center">Mileage </th>
                  <th className="px-4 py-3 text-center">Fuel Consumption (L)</th>
                  <th className="px-4 py-3 text-center">Fuel Price (₹)</th>
                  <th className="px-4 py-3 text-center">Cost (₹)</th>
                  {(isSuperAdmin || isAdmin) && (
                    <th className="px-4 py-3 text-center">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-sm text-gray-700">
                {currentRows.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="px-4 py-12 text-center text-gray-400 font-medium italic">
                      {isTeamLead && assignedInstallerIds.length > 0 
                        ? 'No session records found for your assigned installers. They may not have logged in yet.' 
                        : isTeamLead && assignedInstallerIds.length === 0
                        ? 'No installers are assigned to you.'
                        : 'No installer records found for the selected filters.'}
                    </td>
                  </tr>
                ) : (
                  currentRows.map((item, index) => {
                    const distance = calculateDistance(item.loginOdometerReading, item.logoutOdometerReading);
                    const fuelConsumption = getEffectiveFuelConsumption(item);
                    const isEditing = editingPrices[item.id] !== undefined;
                    const currentFuelPrice = isEditing ? editingPrices[item.id] : item.fuelPrice;
                    const isSaving = savingPrice[item.id] || false;

                    return (
                      <tr key={item.id || index} className="hover:bg-gray-50/80 transition-colors">
                        <td className="px-4 py-3 font-semibold">{item.installerName}</td>
                        <td className="px-4 py-3 text-xs font-mono text-blue-600 whitespace-nowrap">
                          {formatDate(item.loginDateTime)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {item.loginOdometerImage ? (
                            <button
                              onClick={() => handleImageClick(item.loginOdometerImage, 'Login')}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg text-xs font-medium transition border border-indigo-200"
                            >
                              <FaEye className="text-sm" /> View Image
                            </button>
                          ) : (
                            <span className="text-gray-400 text-xs">No Image</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center font-mono text-sm">
                          {item.loginOdometerReading || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-red-500 whitespace-nowrap">
                          {item.logoutDateTime ? formatDate(item.logoutDateTime) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {item.logoutOdometerImage ? (
                            <button
                              onClick={() => handleImageClick(item.logoutOdometerImage, 'Logout')}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-600 rounded-lg text-xs font-medium transition border border-emerald-200"
                            >
                              <FaEye className="text-sm" /> View Image
                            </button>
                          ) : (
                            <span className="text-gray-400 text-xs">No Image</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center font-mono text-sm">
                          {item.logoutOdometerReading || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-3 py-1 text-xs font-bold rounded-full border ${
                            distance !== 'N/A'
                              ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                              : 'bg-gray-50 text-gray-400 border-gray-200'
                          }`}>
                            {distance}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {item.mileage ? (
                            <span className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-medium">
                              {item.mileage} 
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {(() => {
                            const fc = getEffectiveFuelConsumption(item);
                            return fc && fc > 0 ? (
                              <span className="font-medium text-blue-600">
                                {Number(fc).toFixed(2)} L
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {(isSuperAdmin || isAdmin) ? (
                            <div className="flex items-center justify-center gap-1">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={currentFuelPrice || ''}
                                onChange={(e) => handleFuelPriceChange(item.id, e.target.value)}
                                placeholder="Enter price"
                                className="w-20 border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                disabled={isSaving}
                              />
                              {isEditing && (
                                <button
                                  onClick={() => saveFuelPrice(item.id)}
                                  disabled={isSaving}
                                  className="px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-xs font-medium transition disabled:opacity-50"
                                >
                                  {isSaving ? (
                                    <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" />
                                  ) : (
                                    'Save'
                                  )}
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className={`px-2 py-1 rounded font-medium ${
                              item.fuelPrice ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'text-gray-400'
                            }`}>
                              {item.fuelPrice || '-'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {(() => {
                            let cost = item.totalCost;
                            
                            if (!cost || cost === 0) {
                              let fc = item.fuelConsumption;
                              if ((!fc || fc === 0) && item.totalKmTravelled && item.mileage && item.mileage > 0) {
                                fc = item.totalKmTravelled / item.mileage;
                              }
                              
                              if (fc && fc > 0 && item.fuelPrice && item.fuelPrice > 0) {
                                cost = fc * item.fuelPrice;
                              
                              }
                            }
                            
                            return cost && cost > 0 ? (
                              <span className="font-medium text-emerald-600">₹{Number(cost).toFixed(2)}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            );
                          })()}
                        </td>
                        {(isSuperAdmin || isAdmin) && (
                          <td className="px-4 py-3 text-center">
                            {item.fuelPrice && (
                              <span className="text-xs text-green-600 font-medium">✅ Set</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {filteredInstallers.length > 0 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                  <span>Show</span>
                  <select 
                    value={rowsPerPage} 
                    onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    className="border border-gray-300 rounded bg-white px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value={5}>5 entries</option>
                    <option value={10}>10 entries</option>
                    <option value={25}>25 entries</option>
                    <option value={50}>50 entries</option>
                    <option value={100}>100 entries</option>
                  </select>
                  <span>Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, filteredInstallers.length)} of {filteredInstallers.length} rows</span>
                </div>

                <div className="flex items-center gap-1.5">
                  <button 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                    disabled={currentPage === 1}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs font-semibold bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    <FaChevronLeft className="text-[10px]" /> Previous
                  </button>
                  {[...Array(totalPages)].map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentPage(idx + 1)}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition ${
                        currentPage === idx + 1 
                          ? 'bg-indigo-600 border-indigo-600 text-white' 
                          : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                  <button 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                    disabled={currentPage === totalPages}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs font-semibold bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    Next <FaChevronRight className="text-[10px]" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedImage && (
        <ImageViewerModal
          imageUrl={selectedImage}
          imageType={imageType}
          onClose={() => {
            setSelectedImage(null);
            setImageType('');
          }}
        />
      )}

      {/* Leaderboard Modal - Only visible to Super Admin */}
      {isSuperAdmin && showLeaderboardModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[10002] p-4">
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-4xl mx-auto max-h-[80vh] overflow-y-auto text-gray-800">
            <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-3">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FaTrophy className="text-emerald-500" /> Installer Leaderboard
              </h2>
              <button onClick={() => setShowLeaderboardModal(false)} className="text-gray-400 hover:text-gray-600 font-bold">✕</button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-center font-semibold text-gray-600 w-16">Rank</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Installer Name</th>
                    <th className="px-4 py-2 text-center font-semibold text-gray-600">Total Installed</th>
                    <th className="px-4 py-2 text-center font-semibold text-gray-600">Queries Attended</th>
                    <th className="px-4 py-2 text-center font-semibold text-gray-600">Panic Buttons</th>
                    <th className="px-4 py-2 text-center font-semibold text-gray-600">Activity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {leaderboardData.map((item, index) => {
                    let badge = `${index + 1}`;
                    if (index === 0) badge = "🥇 1st";
                    if (index === 1) badge = "🥈 2nd";
                    if (index === 2) badge = "🥉 3rd";

                    const isMe = String(item.username).toLowerCase() === String(currentUser?.username).toLowerCase();

                    return (
                      <tr key={item.id || index} className={`${isMe ? "bg-indigo-50 font-semibold" : index === 0 ? "bg-amber-50/40" : ""} hover:bg-gray-100/80`}>
                        <td className="px-4 py-2.5 text-center font-bold text-gray-700">{badge}</td>
                        <td className="px-4 py-2.5 text-gray-900">
                          {item.username} {isMe && <span className="text-xs bg-indigo-600 text-white px-2 py-0.5 rounded ml-1.5">You</span>}
                        </td>
                        <td className="px-4 py-2.5 text-center font-bold text-indigo-600 text-base">
                          {item.installedCount}
                        </td>
                        <td className="px-4 py-2.5 text-center font-bold text-emerald-600 text-base">
                          {item.queriesAttended}
                        </td>
                        <td className="px-4 py-2.5 text-center font-bold text-orange-600 text-base">
                          {item.totalPanicButtons}
                        </td>
                        <td className="px-4 py-2.5 text-center text-gray-400 text-sm">
                          —
                        </td>
                      </tr>
                    );
                  })}
                  {leaderboardData.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-4 py-4 text-center text-gray-400">No data available to build leaderboard tracks.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstallerReports;